import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
  Label,
  SelectItem,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from '@hap/components'
import { useForm } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { addDays } from 'date-fns'
import { useCallback, useState } from 'react'
import { z } from 'zod'

import { ApiTokenMutators } from '~/mutators/api-tokens'
import { useSettingTitle, SettingsContent } from '~/templates/settings'

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/api-tokens/new',
)({
  component: RouteComponent,
})

const schema = z.object({
  name: z.string().min(1).max(100),
  description: z.string(),
  expiration: z.enum(['30', '90', '365', 'never']),
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const [createdToken, setCreatedToken] = useState<{
    name: string
    token: string
  } | null>(null)

  const { create, isPending } = ApiTokenMutators.useCreate({
    onSuccess: (result) =>
      setCreatedToken({ ...result, name: form.state.values.name }),
  })

  const navigateBack = useCallback(() => {
    navigate({
      to: '/$alias/settings/api-tokens',
    })
  }, [navigate])

  useSettingTitle([
    {
      label: 'API Tokens',
      onClick: navigateBack,
    },
    {
      label: 'New Token',
    },
  ])

  const form = useForm({
    defaultValues: {
      name: 'MCP token',
      description: '',
      expiration: 'never',
    },
    validators: {
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      await create({
        name: value.name,
        description: value.description || undefined,
        audience: 'mcp',
        scopes: ['insights:read'],
        expiresAt:
          value.expiration === 'never'
            ? undefined
            : addDays(new Date(), Number(value.expiration)),
      })
    },
  })

  const copyCreatedToken = async () => {
    if (!createdToken) return
    await navigator.clipboard.writeText(createdToken.token)
    toast.success('API token copied to clipboard')
  }

  return (
    <SettingsContent spacing="sm">
      <Dialog
        open={!!createdToken}
        onOpenChange={(open) => !open && navigateBack()}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>View API Token</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            <p className="text-lg">
              View the <strong>{createdToken?.name}</strong> API token below.
              API tokens should be used for accessing Happitu API from external
              tools.
            </p>
            <p className="text-lg">
              Any action taken by this token will show up as being done by
              yourself.
            </p>
            <div className="flex items-center gap-3 rounded-md border p-3">
              <code className="min-w-0 flex-1 text-lg break-all">
                {createdToken?.token}
              </code>
              <Button variant="ghost" onClick={copyCreatedToken}>
                Copy
              </Button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={navigateBack}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!createdToken && (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-5"
        >
          <div className="grid max-w-200 grid-cols-3 gap-6">
            <form.Field
              name="name"
              children={(field) => {
                return (
                  <label className="col-span-2 grid gap-2">
                    <Label>Name</Label>
                    <Input
                      name={field.name}
                      value={field.state.value}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                  </label>
                )
              }}
            />
            <form.Field
              name="expiration"
              children={(field) => {
                return (
                  <div className="grid gap-2">
                    <Label>Expiration</Label>
                    <Select
                      name={field.name}
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {[
                            ['30', '30 days'],
                            ['90', '90 days'],
                            ['365', '1 year'],
                            ['never', 'Never'],
                          ].map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )
              }}
            />
          </div>
          <form.Field
            name="description"
            children={(field) => {
              return (
                <label className="grid gap-2">
                  <Label required={false}>Description</Label>
                  <Input
                    name={field.name}
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                </label>
              )
            }}
          />

          <Button
            type="submit"
            variant="lime"
            loading={isPending}
            loadingLabel="Creating..."
          >
            Create Token
          </Button>
        </form>
      )}
    </SettingsContent>
  )
}
